import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect
} from "react";
import { AzureIndex } from "@/pages/Messaging/Bots/types";
import useApi from "@/hooks/useApi";
import { AzureIndexesContext } from "@/context/AzureIndexesContext";

export function AzureIndexesProvider(props: { children: React.ReactNode }) {
  const api = useApi();
  const [azureIndexes, setAzureIndexes] = useState<AzureIndex[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.messaging.get(`AzureIndexes/List`);
      if (!resp.ok) {
        setError(`Could not load indexes (${resp.status}). Check your subscription or try refreshing.`);
        setAzureIndexes([]);
        return;
      }
      const indexResult = (await resp.json()) as AzureIndex[];
      setAzureIndexes(Array.isArray(indexResult) ? indexResult : []);
    } catch {
      setError("Could not reach the server. Check your connection and try refreshing.");
      setAzureIndexes([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const add = useCallback(
    async (result: AzureIndex) => {
      const config: AzureIndex = {
        id: result.id,
        name: result.name,
        searchSize: result.searchSize,
        type: result.type,
        siteId: result.siteId,
        semanticSearchEnabled: result.semanticSearchEnabled
      };
      setAzureIndexes([...azureIndexes, config]);
    },
    [azureIndexes]
  );

  const update = useCallback(
    async (result: AzureIndex) => {
      const clone = [...azureIndexes];
      const index = clone.find((index) => index.id === result.id);
      clone[clone.indexOf(index!)] = result;
      setAzureIndexes(clone);
    },
    [azureIndexes]
  );

  const remove = useCallback(
    async (id: number) => {
      const clone = [...azureIndexes];
      const deletedIndex = azureIndexes.find((index) => index.id === id);
      clone.splice(azureIndexes.indexOf(deletedIndex!), 1);
      setAzureIndexes(clone);
    },
    [azureIndexes]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <AzureIndexesContext.Provider
      value={{ azureIndexes, loading, error, add, remove, update }}
    >
      {props.children}
    </AzureIndexesContext.Provider>
  );
}
